/**
 * Pusher Notification Context
 * Provides real-time push notification functionality across the app.
 */
import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import Pusher from 'pusher-js';
import { useAuth } from '@/lib/auth-context';
import { api } from '@/lib/api-client';

const NotificationContext = createContext(null);

// Pusher configuration
const PUSHER_KEY = process.env.REACT_APP_PUSHER_KEY || '995682c407950a5bc098';
const PUSHER_CLUSTER = process.env.REACT_APP_PUSHER_CLUSTER || 'mt1';

export function NotificationProvider({ children }) {
  const { user, token } = useAuth();
  const pusherRef = useRef(null);
  const channelRef = useRef(null);
  
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [connected, setConnected] = useState(false);

  // Fetch existing notifications on mount
  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const response = await api.getNotifications();
      setNotifications(response.notifications || []);
      setUnreadCount(response.unread_count || 0);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  }, [token]);

  // Initialize Pusher connection
  useEffect(() => {
    if (!user?.id || !token) {
      // Cleanup if user logs out
      if (pusherRef.current) {
        pusherRef.current.disconnect();
        pusherRef.current = null;
        channelRef.current = null;
        setConnected(false);
      }
      return;
    }

    // Initialize Pusher
    pusherRef.current = new Pusher(PUSHER_KEY, {
      cluster: PUSHER_CLUSTER,
      forceTLS: true,
    });

    // Subscribe to user's private channel
    const channelName = `private-user-${user.id}`;
    channelRef.current = pusherRef.current.subscribe(channelName);

    // Connection state handlers
    pusherRef.current.connection.bind('connected', () => {
      console.log('Pusher connected');
      setConnected(true);
    });

    pusherRef.current.connection.bind('disconnected', () => {
      console.log('Pusher disconnected');
      setConnected(false);
    });

    pusherRef.current.connection.bind('error', (err) => {
      console.error('Pusher error:', err);
    });

    // Listen for notifications
    channelRef.current.bind('notification', (data) => {
      console.log('Received notification:', data);
      
      // Add to notifications list
      setNotifications(prev => [data, ...prev].slice(0, 50)); // Keep last 50
      setUnreadCount(prev => prev + 1);
      
      // Show toast notification
      showToast(data);
    });

    // Also subscribe to public broadcast channel
    const publicChannel = pusherRef.current.subscribe('public-notifications');
    publicChannel.bind('broadcast', (data) => {
      console.log('Received broadcast:', data);
      setNotifications(prev => [data, ...prev].slice(0, 50));
      showToast(data);
    });

    // Fetch existing notifications
    fetchNotifications();

    // Cleanup on unmount
    return () => {
      if (channelRef.current) {
        channelRef.current.unbind_all();
      }
      if (pusherRef.current) {
        pusherRef.current.unsubscribe(channelName);
        pusherRef.current.unsubscribe('public-notifications');
        pusherRef.current.disconnect();
      }
    };
  }, [user?.id, token, fetchNotifications]);

  // Show toast notification
  const showToast = (notification) => {
    // Dispatch custom event for toast component
    window.dispatchEvent(new CustomEvent('push-notification', {
      detail: notification
    }));
  };

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await api.markNotificationRead(notificationId);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await api.markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  // Clear all notifications
  const clearAll = async () => {
    try {
      await api.clearAllNotifications();
      setNotifications([]);
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to clear notifications:', err);
    }
  };

  // Send test notification (for debugging)
  const sendTestNotification = async () => {
    try {
      await api.testNotification();
    } catch (err) {
      console.error('Failed to send test notification:', err);
    }
  };

  const value = {
    notifications,
    unreadCount,
    connected,
    markAsRead,
    markAllAsRead,
    clearAll,
    sendTestNotification,
    refreshNotifications: fetchNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
}

export default NotificationContext;
